import { CheckSquare, ChevronDown, MoreVertical, Plus, Search } from 'lucide-react';
import {
  type GroupByOption,
  getElevatedPriorityMeta,
  isTerminalStatus,
  type Task,
  type TaskGroup,
  type TaskStatusMeta,
  type WorkspaceMember,
} from '../../lib/task-list';
import { cn } from '../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { UserAvatar } from '../../ui/user-avatar';
import { EmptyState } from '../empty-state';
import {
  TaskAssigneeAvatar,
  TaskCompletionToggle,
  TaskContactChip,
  TaskDueDate,
  TaskPriorityBadge,
  TaskStatusBadge,
} from './task-meta';

export interface TaskListTableProps {
  isLoading: boolean;
  isReadOnly: boolean;
  groupedTasksList: TaskGroup[];
  groupBy: GroupByOption;
  collapsedGroups: Record<string, boolean>;
  onToggleGroup: (key: string) => void;
  statuses: TaskStatusMeta[];
  members?: WorkspaceMember[];
  selectedTaskId?: number;
  onTaskClick: (id: number) => void;
  onMarkComplete: (task: Task) => void;
  onEditTask: (id: number) => void;
  onDeleteTask: (id: number) => void;
  onClearFilters: () => void;
  onCreateTask?: () => void;
  hasActiveFilters?: boolean;
  emptyTitle?: string;
  emptyDescription?: React.ReactNode;
}

function LoadingSkeleton({ isReadOnly }: { isReadOnly: boolean }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-[400px]">Task</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Assignee</TableHead>
            {!isReadOnly && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                </TableCell>
                <TableCell>
                  <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                </TableCell>
                <TableCell>
                  <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                </TableCell>
                <TableCell>
                  <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                </TableCell>
                <TableCell>
                  <div className="h-6 w-6 bg-muted animate-pulse rounded-full" />
                </TableCell>
                {!isReadOnly && (
                  <TableCell className="text-right">
                    <div className="h-8 w-8 ml-auto bg-muted animate-pulse rounded" />
                  </TableCell>
                )}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Loading skeleton, empty state, and the search/status/priority/assignee/contact/due-date-filtered, optionally-grouped task table — the List view rendering shared by both portals' Tasks pages. */
export function TaskListTable({
  isLoading,
  isReadOnly,
  groupedTasksList,
  groupBy,
  collapsedGroups,
  onToggleGroup,
  statuses,
  members,
  selectedTaskId,
  onTaskClick,
  onMarkComplete,
  onEditTask,
  onDeleteTask,
  onClearFilters,
  onCreateTask,
  hasActiveFilters,
  emptyTitle,
  emptyDescription,
}: TaskListTableProps) {
  const totalTasks = groupedTasksList.reduce((sum, g) => sum + g.tasks.length, 0);

  if (isLoading) {
    return <LoadingSkeleton isReadOnly={isReadOnly} />;
  }

  if (totalTasks === 0) {
    const isFiltered = hasActiveFilters ?? true;
    return (
      <EmptyState
        card
        icon={isFiltered ? Search : CheckSquare}
        title={emptyTitle ?? (isFiltered ? 'No tasks match your filters' : 'No tasks yet')}
        description={
          emptyDescription ??
          (isFiltered
            ? 'Try relaxing your search terms or expanding your filter categories.'
            : 'Create tasks to track action items, deadlines, and progress.')
        }
        action={
          isFiltered
            ? {
                label: 'Clear Filters',
                variant: 'outline',
                onClick: onClearFilters,
              }
            : !isReadOnly && onCreateTask
              ? {
                  label: 'Add Task',
                  icon: Plus,
                  onClick: onCreateTask,
                }
              : undefined
        }
      />
    );
  }

  return (
    <>
      {groupedTasksList.map((group) => {
        const isCollapsed = collapsedGroups[group.key];
        if (group.tasks.length === 0) return null;

        return (
          <div key={group.key} className="space-y-3 animate-in fade-in-30 duration-200">
            {groupBy !== 'none' && (
              <button
                type="button"
                onClick={() => onToggleGroup(group.key)}
                className="flex items-center gap-2 group/btn text-sm font-semibold tracking-tight text-foreground/80 hover:text-foreground transition-colors outline-none"
              >
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform duration-200 ease-out',
                    isCollapsed && '-rotate-90'
                  )}
                />
                {groupBy === 'assignee' ? (
                  <div className="flex items-center gap-2">
                    {group.member ? (
                      <UserAvatar user={group.member} size="xs" />
                    ) : (
                      <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[10px] text-muted-foreground font-medium">
                        ?
                      </div>
                    )}
                    <span>{group.title}</span>
                    {group.member?.email && group.member.fullName && (
                      <span className="text-xs text-muted-foreground font-normal">
                        ({group.member.email})
                      </span>
                    )}
                    <Badge
                      variant="secondary"
                      className="px-1.5 py-0.5 rounded-md bg-secondary/40 text-[10px] font-normal text-muted-foreground"
                    >
                      {group.tasks.length} {group.tasks.length === 1 ? 'task' : 'tasks'}
                    </Badge>
                    {group.overdueCount && group.overdueCount > 0 ? (
                      <Badge
                        variant="destructive"
                        className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-destructive/10 text-destructive border-destructive/20"
                      >
                        {group.overdueCount} overdue
                      </Badge>
                    ) : null}
                  </div>
                ) : groupBy === 'status' ? (
                  <div className="flex items-center gap-2">
                    {group.color && (
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: group.color }}
                      />
                    )}
                    <span
                      className={group.colorClass}
                      style={group.color ? { color: group.color } : undefined}
                    >
                      {group.title}
                    </span>
                    <Badge
                      variant="secondary"
                      className="px-1.5 py-0.5 rounded-md bg-secondary/40 text-[10px] font-normal text-muted-foreground"
                    >
                      {group.tasks.length}
                    </Badge>
                  </div>
                ) : (
                  <>
                    <span
                      className={group.colorClass}
                      style={group.color ? { color: group.color } : undefined}
                    >
                      {group.title}
                    </span>
                    <Badge
                      variant="secondary"
                      className="px-1.5 py-0.5 rounded-md bg-secondary/40 text-[10px] font-normal text-muted-foreground"
                    >
                      {group.tasks.length}
                    </Badge>
                  </>
                )}
              </button>
            )}

            {!isCollapsed && (
              <div className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden transition-all duration-300">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[450px]">Task</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Due Date</TableHead>
                      {groupBy !== 'assignee' && <TableHead>Assignee</TableHead>}
                      {!isReadOnly && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.tasks.map((task) => {
                      const done = isTerminalStatus(statuses, task.status);
                      return (
                        <TableRow
                          key={task.id}
                          className={cn(
                            'group transition-colors cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-900/50',
                            selectedTaskId === task.id && 'bg-slate-100/50 dark:bg-slate-900/80'
                          )}
                          onClick={() => onTaskClick(task.id)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-start gap-3">
                              <TaskCompletionToggle done={done} className="mt-0.5" />
                              <div className="flex flex-col">
                                <span
                                  className={cn(
                                    done && 'line-through text-muted-foreground opacity-60'
                                  )}
                                >
                                  {task.label}
                                </span>
                                <TaskContactChip contact={task.contact} className="mt-0.5" />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <TaskStatusBadge statuses={statuses} statusKey={task.status} />
                          </TableCell>
                          <TableCell>
                            {getElevatedPriorityMeta(task.priority) ? (
                              <TaskPriorityBadge priority={task.priority} />
                            ) : (
                              <span className="text-xs text-muted-foreground opacity-50">Low</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <TaskDueDate dueAt={task.dueAt} done={done} showEmpty />
                          </TableCell>
                          {groupBy !== 'assignee' && (
                            <TableCell>
                              <TaskAssigneeAvatar
                                member={
                                  members
                                    ? members.find((m) => m.id === task.assignedTo) || null
                                    : null
                                }
                              />
                            </TableCell>
                          )}

                          {!isReadOnly && (
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => onMarkComplete(task)}>
                                    Mark as Complete
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => onEditTask(task.id)}>
                                    Edit Task
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => onDeleteTask(task.id)}
                                  >
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
